import React, { useState, useEffect, useCallback, useRef } from 'react';
import { csvCell } from '../../lib/csvCell';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import {
  Search, Plus, X, Edit3, Eye, Trash2, Upload, CheckCircle, Clock, Check, Share2,
  AlertTriangle, FileText, Users, ChevronDown, ChevronUp, Download,
  Filter, RefreshCw, Briefcase, Building, MapPin, Calendar, DollarSign,
  Link as LinkIcon, Sparkles, List, UserCheck, Archive, ChevronLeft, ChevronRight, Send, AlertCircle,
} from 'lucide-react';
import { Job, JobApplication, JobStatus } from '../../types';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import ImproveTextDropdown from '../shared/ImproveTextDropdown';
import Modal from '../shared/Modal';

type AdminJobView = 'jobs' | 'interests';

const JOB_STATUSES: { value: JobStatus; label: string; color: string }[] = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  { value: 'pending_review', label: 'Pending Review', color: 'bg-amber-100 text-amber-700' },
  { value: 'published', label: 'Published', color: 'bg-blue-100 text-brand' },
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'in_review', label: 'In Review', color: 'bg-purple-100 text-purple-700' },
  { value: 'filled', label: 'Filled', color: 'bg-teal-100 text-teal-700' },
  { value: 'closed', label: 'Closed', color: 'bg-red-100 text-red-700' },
  { value: 'archived', label: 'Archived', color: 'bg-slate-200 text-slate-500' },
];

const APPLICATION_STATUSES = [
  { value: 'interested', label: 'Interested', color: 'bg-blue-100 text-brand' },
  { value: 'applied', label: 'Applied', color: 'bg-slate-100 text-slate-700' },
  { value: 'pending_applicant_information', label: 'Pending Applicant Information', color: 'bg-orange-100 text-orange-700' },
  { value: 'under_review', label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  { value: 'shortlisted', label: 'Shortlisted', color: 'bg-purple-100 text-purple-700' },
  { value: 'interviewing', label: 'Interviewing', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'interview_invitation_sent', label: 'Interview Invitation Sent', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-teal-100 text-teal-700' },
  { value: 'interview_completed', label: 'Interview Completed', color: 'bg-sky-100 text-sky-700' },
  { value: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'shared_with_employer', label: 'Shared with Employer', color: 'bg-blue-100 text-brand' },
  { value: 'offer_sent', label: 'Offer Sent', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'hired', label: 'Hired', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
  { value: 'offer_declined', label: 'Offer Declined', color: 'bg-rose-100 text-rose-700' },
  { value: 'assigned', label: 'Assigned', color: 'bg-teal-100 text-teal-700' },
  { value: 'applicant_withdrawn', label: 'Applicant Withdrawn', color: 'bg-slate-100 text-slate-500' },
];

const JOB_TYPES = ['full-time', 'part-time', 'seasonal', 'internship', 'temporary', 'contract'];

function StatusBadge({ status }: { status: string }) {
  const s = JOB_STATUSES.find(st => st.value === status);
  if (!s) return <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-500">{status}</span>;
  return <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${s.color}`}>{s.label}</span>;
}

function AppStatusBadge({ status }: { status: string }) {
  const s = APPLICATION_STATUSES.find(st => st.value === status);
  if (!s) return <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-500">{status}</span>;
  return <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${s.color}`}>{s.label}</span>;
}

interface JobsManagerProps {
  onViewApplicantProfile?: (applicationData: any) => void;
  canEdit?: boolean;
}

export default function JobsManager({ onViewApplicantProfile, canEdit = true }: JobsManagerProps = {}) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [programFilter, setProgramFilter] = useState<string[]>([]);
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  // ADM-04: the per job Change Status menu is click driven (one open at a
  // time), following the AdminDashboard profile dropdown pattern.
  const [statusMenuJobId, setStatusMenuJobId] = useState<string | null>(null);
  useEffect(() => {
    if (!statusMenuJobId) return;
    const onDocClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-job-status-menu]')) setStatusMenuJobId(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [statusMenuJobId]);
  const [sortOrder, setSortOrder] = useState<string>('newest');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [showApplications, setShowApplications] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: '', employerId: '', employerName: '', location: '', jobType: 'full-time' as string,
    description: '', requirements: '', salary: '', compensation: '',
    startDate: '', endDate: '', applicationDeadline: '',
    country: '', programType: '', programs: [] as string[], industry: '', positions: 1, tags: '',
    imageUrl: '', supplierCode: '',
  });

  // Employer search state
  const [employers, setEmployers] = useState<{ id: string; companyName: string }[]>([]);
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const empRef = useRef<HTMLDivElement>(null);

  // View mode toggle
  const [viewMode, setViewMode] = useState<AdminJobView>('jobs');

  // All interests / matches state
  const [allMatches, setAllMatches] = useState<any[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchSearch, setMatchSearch] = useState('');
  const [matchCompanyFilter, setMatchCompanyFilter] = useState<string>('all');
  const [matchStatusFilter, setMatchStatusFilter] = useState<string>('all');
  const [matchProgramFilter, setMatchProgramFilter] = useState<string[]>([]);
  const [matchSupplierFilter, setMatchSupplierFilter] = useState<string>('all');
  const [matchSharedFilter, setMatchSharedFilter] = useState<'all' | 'shared' | 'not_shared'>('all');
  // Pagination for matches table
  const [matchPage, setMatchPage] = useState(1);
  const [matchPageSize, setMatchPageSize] = useState(50);
  // Group by applicant toggle
  const [groupByApplicant, setGroupByApplicant] = useState(false);

  // Application preview modal state
  const [previewApp, setPreviewApp] = useState<any>(null);
  // Applications table search, filter, pagination
  const [appSearch, setAppSearch] = useState('');
  const [appStatusFilter, setAppStatusFilter] = useState<string>('all');
  const [appTypeFilter, setAppTypeFilter] = useState<string>('all');
  const [appPage, setAppPage] = useState(1);
  const APP_PAGE_SIZE = 50;
  const [profileLoading, setProfileLoading] = useState<string | null>(null); // appId while loading

  // Multi-select for applications modal (per-job)
  const [selectedAppModalIds, setSelectedAppModalIds] = useState<Set<string>>(new Set());

  // Multi-select for matches table
  const [selectedMatchIds, setSelectedMatchIds] = useState<Set<string>>(new Set());
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Import state
  const [importStep, setImportStep] = useState<'upload' | 'mapping' | 'confirm'>('upload');
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvPreview, setCsvPreview] = useState<Record<string, string>[]>([]);
  const [fieldMapping, setFieldMapping] = useState<{ csvColumn: string; jobField: string }[]>([]);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  // Company & Programs applied to ALL imported jobs
  const [importEmployerId, setImportEmployerId] = useState('');
  const [importEmployerName, setImportEmployerName] = useState('');
  const [importEmployerSearch, setImportEmployerSearch] = useState('');
  const [showImportEmployerDropdown, setShowImportEmployerDropdown] = useState(false);
  const importEmployerRef = useRef<HTMLDivElement>(null);
  const [importPrograms, setImportPrograms] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const dragCounter = useRef(0);

  // Available job schema fields for mapping
  const JOB_FIELDS = [
    { value: '_skip', label: '— Skip column —' },
    { value: 'id', label: 'Job ID' },
    { value: 'title', label: 'Title *' },
    { value: 'employerName', label: 'Employer Name' },
    { value: 'employerId', label: 'Employer ID' },
    { value: 'location', label: 'Location' },
    { value: 'jobType', label: 'Job Type' },
    { value: 'description', label: 'Description' },
    { value: 'requirements', label: 'Requirements' },
    { value: 'salary', label: 'Salary' },
    { value: 'compensation', label: 'Compensation' },
    { value: 'startDate', label: 'Start Date' },
    { value: 'endDate', label: 'End Date' },
    { value: 'applicationDeadline', label: 'Application Deadline' },
    { value: 'country', label: 'Country' },
    { value: 'programType', label: 'Program Type' },
    { value: 'industry', label: 'Industry' },
    { value: 'positions', label: 'Positions' },
    { value: 'tags', label: 'Tags' },
    { value: 'imageUrl', label: 'URL Photo' },
    { value: 'supplierCode', label: 'Supplier Code' },
    { value: 'status', label: 'Status' },
  ];

  // Programs from Firestore
  const [programs, setPrograms] = useState<string[]>([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  // AI Job Creation
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  useBodyScrollLock(showCreateModal || showImportModal || showApplications || showAiModal);
  const [importing, setImporting] = useState(false);

  // ── Bulk action state ───────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkModal, setBulkModal] = useState<{ type: 'visibility' | 'status' | 'company' | 'supplier' | 'delete' | 'archive'; visible?: boolean; status?: string } | null>(null);
  const [bulkCompanyId, setBulkCompanyId] = useState('');
  const [bulkCompanyName, setBulkCompanyName] = useState('');
  const [bulkCompanySearch, setBulkCompanySearch] = useState('');
  const [showBulkCompanyDropdown, setShowBulkCompanyDropdown] = useState(false);
  const [bulkSupplierCode, setBulkSupplierCode] = useState('');
  const bulkCompanyRef = useRef<HTMLDivElement>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    notifyToast(type, message);
  };

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safeFetch('/api/admin/jobs');
      setJobs(data.jobs || []);
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPrograms = useCallback(async () => {
    setProgramsLoading(true);
    try {
      const data = await safeFetch('/api/admin/programs');
      setPrograms((data.programs || []).map((p: any) => p.name).sort());
    } catch { setPrograms([]); }
    finally { setProgramsLoading(false); }
  }, []);

  useEffect(() => { fetchJobs(); fetchPrograms(); }, [fetchJobs, fetchPrograms]);

  // Close employer dropdowns on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (empRef.current && !empRef.current.contains(e.target as Node)) setShowEmpDropdown(false);
      if (bulkCompanyRef.current && !bulkCompanyRef.current.contains(e.target as Node)) setShowBulkCompanyDropdown(false);
      if (importEmployerRef.current && !importEmployerRef.current.contains(e.target as Node)) setShowImportEmployerDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Session 6 item 9: the employer list was refetched on EVERY focus
  // of the three employer dropdowns (create form, import, bulk
  // actions). It is a page-session list — load it once; a failed load
  // clears the marker so the next focus retries.
  const employersRequestedRef = useRef(false);
  const fetchEmployers = useCallback(async () => {
    if (employersRequestedRef.current) return;
    employersRequestedRef.current = true;
    try {
      const data = await safeFetch('/api/admin/employers');
      setEmployers((data.employers || []).map((e: any) => ({ id: e.id, companyName: e.companyName })));
    } catch { employersRequestedRef.current = false; }
  }, []);

  const resetForm = () => {
    setForm({ title: '', employerId: '', employerName: '', location: '', jobType: 'full-time',
      description: '', requirements: '', salary: '', compensation: '',
      startDate: '', endDate: '', applicationDeadline: '',
      country: '', programType: '', programs: [], industry: '', positions: 1, tags: '', imageUrl: '', supplierCode: '' });
    setEditingJob(null);
    setEmpSearch('');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        programs: form.programs,
        tags: form.tags ? form.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [],
      };
      if (editingJob) {
        await safeFetch(`/api/admin/jobs/${editingJob.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        showToast('Job updated successfully', 'success');
      } else {
        await safeFetch('/api/admin/jobs', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        showToast('Job created successfully', 'success');
      }
      setShowCreateModal(false); resetForm(); fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog('Are you sure you want to delete this job? This cannot be undone.'))) return;
    try {
      await safeFetch(`/api/admin/jobs/${id}`, { method: 'DELETE' });
      showToast('Job deleted', 'success');
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await safeFetch(`/api/admin/jobs/${id}/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      showToast(`Status updated to ${status}`, 'success');
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleVisibilityToggle = async (id: string, visible: boolean) => {
    try {
      await safeFetch(`/api/admin/jobs/${id}/visibility`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible }),
      });
      showToast(`Job ${visible ? 'visible' : 'hidden'} on job board`, 'success');
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  // ── Bulk action handlers ───────────────────────────────────────────
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredJobs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredJobs.map((j: any) => j.id)));
    }
  };

  const selectAllPage = () => {
    const pageJobs = filteredJobs.slice(0, page * PAGE_SIZE);
    setSelectedIds(new Set(pageJobs.map((j: any) => j.id)));
  };

  const executeBulkVisibility = async (visible: boolean) => {
    if (selectedIds.size === 0) return;
    try {
      await safeFetch('/api/admin/jobs/bulk/visibility', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), visible }),
      });
      showToast(`${selectedIds.size} jobs updated`, 'success');
      setSelectedIds(new Set());
      setBulkModal(null);
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const executeBulkStatus = async (status: string) => {
    if (selectedIds.size === 0) return;
    try {
      await safeFetch('/api/admin/jobs/bulk/status', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), status }),
      });
      showToast(`${selectedIds.size} jobs updated to ${status}`, 'success');
      setSelectedIds(new Set());
      setBulkModal(null);
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const executeBulkCompany = async () => {
    if (selectedIds.size === 0 || !bulkCompanyId) return;
    try {
      await safeFetch('/api/admin/jobs/bulk/company', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), employerId: bulkCompanyId, employerName: bulkCompanyName }),
      });
      showToast(`${selectedIds.size} jobs reassigned to ${bulkCompanyName}`, 'success');
      setSelectedIds(new Set());
      setBulkModal(null);
      setBulkCompanyId(''); setBulkCompanyName('');
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const executeBulkSupplier = async () => {
    if (selectedIds.size === 0 || !bulkSupplierCode.trim()) return;
    try {
      await safeFetch('/api/admin/jobs/bulk/supplier', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), supplierCode: bulkSupplierCode.trim() }),
      });
      showToast(`${selectedIds.size} jobs updated to supplier code "${bulkSupplierCode.trim()}"`, 'success');
      setSelectedIds(new Set());
      setBulkModal(null);
      setBulkSupplierCode('');
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const executeBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await safeFetch('/api/admin/jobs/bulk/delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      showToast(`${selectedIds.size} jobs deleted`, 'success');
      setSelectedIds(new Set());
      setBulkModal(null);
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const executeBulkArchive = async () => {
    if (selectedIds.size === 0) return;
    try {
      await safeFetch('/api/admin/jobs/bulk/status', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), status: 'archived' }),
      });
      showToast(`${selectedIds.size} jobs archived`, 'success');
      setSelectedIds(new Set());
      setBulkModal(null);
      fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  // ── CSV Export ──────────────────────────────────────────────────────
  const exportCSV = () => {
    if (jobs.length === 0) return;
    const fields = [
      'id', 'title', 'employerName', 'employerId', 'location', 'jobType',
      'description', 'requirements', 'salary', 'compensation',
      'startDate', 'endDate', 'applicationDeadline', 'country', 'programType',
      'industry', 'positions', 'imageUrl', 'supplierCode', 'status', 'visible', 'applicantCount', 'assignedCount',
      'createdAt', 'updatedAt',
    ];
    const headers = fields.map(f => f.replace(/([A-Z])/g, '_$1').toUpperCase());
    const rows = jobs.map((job: any) =>
      fields.map(f => {
        let val = job[f];
        if (Array.isArray(val)) val = val.join('; ');
        if (val === undefined || val === null) val = '';
        // Wave 5 (M-05): shared csvCell — quoting + formula-injection guard.
        return csvCell(String(val));
      })
    );
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jobs_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${jobs.length} jobs to CSV`, 'success');
  };

  // ── CSV Parsing ────────────────────────────────────────────────────
  const parseCSV = (text: string): string[][] => {
    const rows: string[][] = [];
    let current = '';
    let inQuotes = false;
    let row: string[] = [];
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') { inQuotes = !inQuotes; continue; }
      if (ch === ',' && !inQuotes) { row.push(current.trim()); current = ''; continue; }
      if ((ch === '\n' || (ch === '\r' && text[i + 1] === '\n')) && !inQuotes) {
        if (ch === '\r') i++; // skip \n after \r
        row.push(current.trim());
        if (row.some(c => c)) rows.push(row);
        current = ''; row = [];
        continue;
      }
      if (ch === '\r' && !inQuotes) { row.push(current.trim()); current = ''; continue; }
      current += ch;
    }
    if (current.trim() || row.length > 0) { row.push(current.trim()); if (row.some(c => c)) rows.push(row); }
    return rows;
  };

  const handleFileSelect = (file: File | undefined) => {
    if (!file) return;
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const rows = parseCSV(text);
      if (rows.length < 2) { showToast('CSV must have a header row and at least one data row', 'error'); return; }
      const headers = rows[0];
      const data = rows.slice(1).map(row => {
        const obj: Record<string, string> = {};
        headers.forEach((h, i) => { obj[h] = row[i] || ''; });
        return obj;
      });
      setCsvHeaders(headers);
      setCsvRows(rows);
      setCsvPreview(data.slice(0, 5));
      // Auto-map: try to match CSV headers to job fields by common names
      const autoMap: { csvColumn: string; jobField: string }[] = headers.map(h => {
        const normalized = h.toLowerCase().replace(/[\s_-]/g, '');
        const match: Record<string, string> = {
          'id': 'id', 'jobid': 'id', 'job_id': 'id',
          'title': 'title', 'jobtitle': 'title', 'position': 'title', 'job': 'title',
          'employer': 'employerName', 'company': 'employerName', 'employername': 'employerName', 'companyname': 'employerName',
          'employerid': 'employerId', 'employer_id': 'employerId',
          'location': 'location', 'city': 'location', 'address': 'location',
          'jobtype': 'jobType', 'type': 'jobType', 'employmenttype': 'jobType', 'employment_type': 'jobType',
          'description': 'description', 'desc': 'description', 'jobdescription': 'description',
          'requirements': 'requirements', 'reqs': 'requirements', 'qualifications': 'requirements', 'qualification': 'requirements',
          'salary': 'salary', 'pay': 'salary', 'compensation': 'salary', 'wage': 'salary',
          'startdate': 'startDate', 'start_date': 'startDate', 'start': 'startDate',
          'enddate': 'endDate', 'end_date': 'endDate', 'end': 'endDate',
          'deadline': 'applicationDeadline', 'applicationdeadline': 'applicationDeadline', 'application_deadline': 'applicationDeadline', 'closingdate': 'applicationDeadline',
          'country': 'country', 'nation': 'country',
          'programtype': 'programType', 'program': 'programType', 'program_type': 'programType',
          'industry': 'industry', 'sector': 'industry',
          'suppliercode': 'supplierCode', 'supplier_code': 'supplierCode', 'supplier': 'supplierCode', 'partnercode': 'supplierCode',
          'positions': 'positions', 'opening': 'positions', 'vacancies': 'positions',
          'tags': 'tags', 'tag': 'tags',
          'status': 'status', 'jobstatus': 'status',
        };
        return { csvColumn: h, jobField: match[normalized] || '_skip' };
      });
      setFieldMapping(autoMap);
      setImportStep('mapping');
    };
    reader.readAsText(file);
  };

  const handleImportFromMapping = async () => {
    const mappedFields = fieldMapping.filter(m => m.jobField !== '_skip');
    if (!mappedFields.some(m => m.jobField === 'title')) {
      showToast('You must map a column to Title', 'error'); return;
    }
    setImporting(true);
    try {
      // Build rows as objects keyed by CSV column name
      const rows = csvRows.slice(1).map(row => {
        const obj: Record<string, string> = {};
        csvHeaders.forEach((h, i) => { obj[h] = row[i] || ''; });
        return obj;
      });
      const data = await safeFetch('/api/admin/jobs/import', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rows, fieldMapping: mappedFields, updateExisting,
          importEmployerId: importEmployerId || undefined,
          importEmployerName: importEmployerName || undefined,
          importPrograms: importPrograms.length > 0 ? importPrograms : undefined,
        }),
      });
      const parts: string[] = [];
      if (data.imported > 0) parts.push(`Imported ${data.imported} new`);
      if (data.updated > 0) parts.push(`Updated ${data.updated} existing`);
      if (data.errors?.length) parts.push(`${data.errors.length} errors`);
      showToast(parts.join(', ') || 'Import complete', 'success');
      if (data.errors?.length) console.warn('Import errors:', data.errors);
      resetImportModal(); fetchJobs();
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setImporting(false); }
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setImportStep('upload');
    setCsvRows([]);
    setCsvHeaders([]);
    setCsvPreview([]);
    setFieldMapping([]);
    setUpdateExisting(false);
    setImportFileName('');
    setImportEmployerId('');
    setImportEmployerName('');
    setImportEmployerSearch('');
    setImportPrograms([]);
    setDragOver(false);
    dragCounter.current = 0;
  };

  const handleEdit = (job: Job) => {
    setForm({
      title: job.title, employerId: job.employerId || '', employerName: job.employerName,
      location: job.location, jobType: job.jobType, description: job.description || '',
      requirements: job.requirements || '', salary: job.salary || '', compensation: job.compensation || '',
      startDate: job.startDate || '', endDate: job.endDate || '', applicationDeadline: job.applicationDeadline || '',
      country: job.country || '', programType: job.programType || '',
      programs: job.programs || [],
      industry: job.industry || '',
      positions: job.positions || 1, tags: (job.tags || []).join(', '),
      imageUrl: (job as any).imageUrl || '',
      supplierCode: (job as any).supplierCode || '',
    });
    setEditingJob(job);
    setShowCreateModal(true);
  };

  const handleShareApplicationInModal = async (app: any) => {
    try {
      const endpoint = app._type === 'match'
        ? `/api/admin/matches/${app.id}/share`
        : `/api/admin/applications/${app.id}/share`;
      await safeFetch(endpoint, { method: 'POST' });
      showToast('Shared with employer successfully', 'success');
      // Refresh applications list in the modal
      if (selectedJob) viewApplications(selectedJob);
      // Refresh the global matches table too
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleRequestInfoInModal = async (app: any) => {
    try {
      await safeFetch(`/api/admin/applications/${app.id}/request-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: 'Please provide additional information as requested by the administrator.' }),
      });
      showToast('Additional information requested', 'success');
      if (selectedJob) viewApplications(selectedJob);
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleBulkShareApplicationsInModal = async () => {
    if (selectedAppModalIds.size === 0) return;
    setBulkActionLoading(true);
    try {
      const items = applications
        .filter(a => selectedAppModalIds.has(`${a._type || 'application'}-${a.id}`))
        .map((a: any) => ({ id: a.id, _type: a._type || 'application' }));
      const data = await safeFetch('/api/admin/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      showToast(`Shared ${data.shared || 0} applicant(s) with employer(s)`, 'success');
      setSelectedAppModalIds(new Set());
      if (selectedJob) viewApplications(selectedJob);
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setBulkActionLoading(false); }
  };

  const toggleAppModalSelect = (id: string) => {
    setSelectedAppModalIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const viewApplications = async (job: Job) => {
    setSelectedJob(job);
    setApplications([]);
    setSelectedAppModalIds(new Set());
    try {
      const data = await safeFetch(`/api/admin/jobs/${job.id}/applications`);
      // Combine applications and matches into a single list
      const apps = (data.applications || []).map((a: any) => ({ ...a, _type: 'application' }));
      const matches = (data.matches || []).map((m: any) => ({ ...m, _type: 'match' }));
      // Merge and sort by date descending
      const combined = [...apps, ...matches].sort((a: any, b: any) => {
        const aDate = a.appliedAt || a.createdAt || '';
        const bDate = b.appliedAt || b.createdAt || '';
        return bDate.localeCompare(aDate);
      });
      setApplications(combined);
      setShowApplications(true);
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleAppStatusChange = async (app: any, status: string) => {
    try {
      const appId = app.id || app.applicationId;
      const type = app._type || 'application';
      await safeFetch(`/api/admin/applications/${appId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, type }),
      });
      showToast(`Application status updated to "${status}"`, 'success');
      // Optimistic UI update
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status } : a));
      // Refresh
      if (selectedJob) viewApplications(selectedJob);
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const uniqueCompanies = [...new Set(jobs.map((j: any) => j.employerName).filter(Boolean))].sort();
  const uniqueSupplierCodes = [...new Set(jobs.map((j: any) => j.supplierCode).filter(Boolean))].sort();
  const displayPrograms = programs.length > 0 ? programs : [...new Set(jobs.flatMap((j: any) => j.programs || []).filter(Boolean))].sort();

  // Filtered applications for the applications modal
  const filteredApps = applications.filter((a: any) => {
    if (appStatusFilter !== 'all' && a.status !== appStatusFilter) return false;
    if (appTypeFilter !== 'all' && a._type !== appTypeFilter) return false;
    if (appSearch) {
      const q = appSearch.toLowerCase();
      const name = (a.applicantName || '').toLowerCase();
      const firstName = (a.applicantFirstName || '').toLowerCase();
      const lastName = (a.applicantLastName || '').toLowerCase();
      const email = (a.applicantEmail || '').toLowerCase();
      const id = (a.id || '').toLowerCase();
      const primaryId = (a.primaryApplicationId || '').toLowerCase();
      const country = (a.country || '').toLowerCase();
      const program = (a.programType || '').toLowerCase();
      if (!name.includes(q) && !firstName.includes(q) && !lastName.includes(q) && !email.includes(q) && !id.includes(q) && !primaryId.includes(q) && !country.includes(q) && !program.includes(q)) return false;
    }
    return true;
  });

  const filteredJobs = jobs
    .filter((j: any) => {
      if (statusFilter !== 'all' && j.status !== statusFilter) return false;
      if (companyFilter !== 'all' && j.employerName !== companyFilter) return false;
      if (supplierFilter !== 'all' && (j.supplierCode || '') !== supplierFilter) return false;
      if (programFilter.length > 0) {
        const jobPrograms = j.programs || (j.programType ? [j.programType] : []);
        if (!jobPrograms.some((p: string) => programFilter.includes(p))) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return j.title.toLowerCase().includes(q) || j.employerName.toLowerCase().includes(q) || j.location.toLowerCase().includes(q) || (j.supplierCode || '').toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a: any, b: any) => {
      switch (sortOrder) {
        case 'oldest':
          return (a.createdAt || '').localeCompare(b.createdAt || '');
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'title-desc':
          return (b.title || '').localeCompare(a.title || '');
        case 'status-asc':
          return (a.status || '').localeCompare(b.status || '');
        case 'status-desc':
          return (b.status || '').localeCompare(a.status || '');
        case 'newest':
        default:
          return (b.createdAt || '').localeCompare(a.createdAt || '');
      }
    });

  // ── All Matches / Interests ──
  const fetchAllMatches = useCallback(async () => {
    setMatchesLoading(true);
    try {
      const data = await safeFetch('/api/admin/matches/all');
      setAllMatches(data.matches || []);
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setMatchesLoading(false); }
  }, []);

  // ── Export matches as CSV ──
  const exportMatchesCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Application ID', 'Program', 'Status', 'Employer', 'Job ID', 'Job Title'];
    const rows = filteredMatches.map((m: any) => [
      m.applicantFirstName || m.applicantName?.split(' ')[0] || '',
      m.applicantLastName || (m.applicantName?.includes(' ') ? m.applicantName.split(' ').slice(1).join(' ') : '') || '',
      m.applicantEmail || '',
      m.applicationId || m.id || '',
      m.programType || '',
      m.status || '',
      m.employerName || '',
      m.jobId || '',
      m.jobTitle || '',
    ].map(v => csvCell(v)).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `interests-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Status change with notification prompt ──
  const handleMatchStatusChange = async (m: any, newStatus: string) => {
    if (m.status === newStatus) return;
    const sendNotif = await confirmDialog(`Send notification email to applicant about this status change?`);
    try {
      await safeFetch(`/api/admin/matches/${m.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, sendNotification: sendNotif }),
      });
      showToast(`Status updated to "${newStatus}"${sendNotif ? ' — notification sent' : ''}`, 'success');
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleInterestStatusChange = async (m: any, newStatus: string) => {
    if (m.status === newStatus) return;
    const sendNotif = await confirmDialog(`Send notification email to applicant about this status change?`);
    try {
      const type = m._type || 'application';
      await safeFetch(`/api/admin/applications/${m.id}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, type, sendNotification: sendNotif }),
      });
      showToast(`Status updated to "${newStatus}"${sendNotif ? ' — notification sent' : ''}`, 'success');
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleShareWithEmployer = async (m: any) => {
    try {
      const endpoint = m._type === 'match' ? `/api/admin/matches/${m.id}/share` : `/api/admin/applications/${m.id}/share`;
      await safeFetch(endpoint, { method: 'POST' });
      showToast('Shared with employer successfully', 'success');
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleBulkShareWithEmployer = async () => {
    if (selectedMatchRecords.length === 0) return;
    setBulkActionLoading(true);
    try {
      const items = selectedMatchRecords.map((m: any) => ({ id: m.id, _type: m._type || 'application' }));
      const data = await safeFetch('/api/admin/share', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      showToast(`Shared ${data.shared || 0} applicant(s) with employer(s)`, 'success');
      setSelectedMatchIds(new Set());
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setBulkActionLoading(false); }
  };

  // ── Multi-select handlers (defined here; these reference only hooks, not derived vars) ──
  const toggleMatchSelect = (id: string) => {
    setSelectedMatchIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // toggleSelectAllMatches, selectedMatchRecords, handleBulkArchive, handleBulkStatusChange
  // are defined later after filteredMatches for TDZ safety.
  const uniqueMatchCompanies = [...new Set(allMatches.map((m: any) => m.employerName).filter(Boolean))].sort();
  const uniqueMatchPrograms = [...new Set(allMatches.map((m: any) => m.programType).filter(Boolean))].sort();
  const uniqueMatchSupplierCodes = [...new Set(allMatches.map((m: any) => m.supplierCode).filter(Boolean))].sort();

  const filteredMatches = allMatches.filter((m: any) => {
    if (matchStatusFilter !== 'all' && m.status !== matchStatusFilter) return false;
    if (matchCompanyFilter !== 'all' && m.employerName !== matchCompanyFilter) return false;
    if (matchProgramFilter.length > 0 && !matchProgramFilter.includes(m.programType)) return false;
    if (matchSupplierFilter !== 'all' && (m.supplierCode || '') !== matchSupplierFilter) return false;
    if (matchSharedFilter === 'shared' && m.sharedWithEmployer !== true) return false;
    if (matchSharedFilter === 'not_shared' && m.sharedWithEmployer === true) return false;
    if (matchSearch) {
      const q = matchSearch.toLowerCase();
      const searchable = [
        m.id || '', m.jobTitle || '', m.employerName || '',
        m.applicantName || '', m.applicantEmail || '',
        m.applicantFirstName || '', m.applicantLastName || '',
        m.programType || '', m.notes || '', m.supplierCode || '',
      ].join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    return true;
  });

  // ── Pagination & Grouping for matches ──
  // Reset to page 1 when filters change
  const matchFilterKey = JSON.stringify({ matchSearch, matchStatusFilter, matchCompanyFilter, matchProgramFilter, matchSupplierFilter, matchSharedFilter });
  React.useEffect(() => { setMatchPage(1); }, [matchFilterKey]);

  // Group by applicant
  const groupedMatches = React.useMemo(() => {
    if (!groupByApplicant) return null;
    const groups = new Map<string, any[]>();
    for (const m of filteredMatches) {
      const key = m.applicantId || m.applicantEmail || m.applicantName || 'unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(m);
    }
    return Array.from(groups.entries())
      .map(([applicantId, items]) => ({
        applicantId,
        applicantName: items[0].applicantName || '—',
        applicantEmail: items[0].applicantEmail || '',
        profilePhotoUrl: items[0].profilePhotoUrl || '',
        programType: items[0].programType || '—',
        items,
      }))
      .sort((a, b) => a.applicantName.localeCompare(b.applicantName));
  }, [filteredMatches, groupByApplicant]);

  const totalPages = groupByApplicant
    ? Math.ceil((groupedMatches?.length || 0) / matchPageSize)
    : Math.ceil(filteredMatches.length / matchPageSize);
  const paginatedMatches = groupByApplicant
    ? (groupedMatches || []).slice((matchPage - 1) * matchPageSize, matchPage * matchPageSize)
    : filteredMatches.slice((matchPage - 1) * matchPageSize, matchPage * matchPageSize);

  // ── Derived vars & handlers that depend on filteredMatches (TDZ-safe here) ──
  const toggleSelectAllMatches = () => {
    if (selectedMatchIds.size === filteredMatches.length) {
      setSelectedMatchIds(new Set());
    } else {
      setSelectedMatchIds(new Set(filteredMatches.map((m: any) => m.id)));
    }
  };

  const selectedMatchRecords = filteredMatches.filter((m: any) => selectedMatchIds.has(m.id));

  const handleBulkArchive = async () => {
    if (selectedMatchRecords.length === 0) return;
    if (!(await confirmDialog(`Archive ${selectedMatchRecords.length} selected record${selectedMatchRecords.length !== 1 ? 's' : ''}?`))) return;
    const sendNotif = await confirmDialog(`Send notification email${selectedMatchRecords.length !== 1 ? 's' : ''} to the affected applicant${selectedMatchRecords.length !== 1 ? 's' : ''}?`);
    setBulkActionLoading(true);
    try {
      const data = await safeFetch('/api/admin/matches/archive-all', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedMatchRecords.map((m: any) => ({ id: m.id, _type: m._type })) }),
      });
      if (sendNotif) {
        const notifResults = await Promise.allSettled(
          selectedMatchRecords.map((m: any) => {
            const endpoint = m._type === 'match'
              ? `/api/admin/matches/${m.id}`
              : `/api/admin/applications/${m.id}/status`;
            const body = m._type === 'match'
              ? { status: m._type === 'match' ? 'closed' : 'rejected', sendNotification: true }
              : { status: 'rejected', type: m._type || 'application', sendNotification: true };
            return safeFetch(endpoint, {
              method: m._type === 'match' ? 'PUT' : 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
          })
        );
        const sent = notifResults.filter(r => r.status === 'fulfilled').length;
        showToast(`Archived ${data.archived} — ${sent} notification${sent !== 1 ? 's' : ''} sent`, 'success');
      } else {
        showToast(`Archived ${data.archived} record${data.archived !== 1 ? 's' : ''}${data.errors ? ` (${data.errors} errors)` : ''}`, 'success');
      }
      setSelectedMatchIds(new Set());
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setBulkActionLoading(false); }
  };

  const handleBulkStatusChange = async () => {
    if (selectedMatchRecords.length === 0 || !bulkStatusValue) return;
    const sendNotif = await confirmDialog(`Send notification email${selectedMatchRecords.length !== 1 ? 's' : ''} to the affected applicant${selectedMatchRecords.length !== 1 ? 's' : ''}?`);
    setBulkActionLoading(true);
    try {
      let updated = 0;
      for (const m of selectedMatchRecords) {
        try {
          if (m._type === 'match') {
            await safeFetch(`/api/admin/matches/${m.id}`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: bulkStatusValue, sendNotification: sendNotif }),
            });
          } else {
            await safeFetch(`/api/admin/applications/${m.id}/status`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: bulkStatusValue, type: m._type || 'application', sendNotification: sendNotif }),
            });
          }
          updated++;
        } catch { /* skip individual failures */ }
      }
      showToast(`Updated ${updated} record${updated !== 1 ? 's' : ''} to "${bulkStatusValue}"${sendNotif ? ' — notifications sent' : ''}`, 'success');
      setSelectedMatchIds(new Set());
      setShowBulkStatusModal(false);
      setBulkStatusValue('');
      fetchAllMatches();
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setBulkActionLoading(false); }
  };

  const MATCH_STATUSES = [
    { value: 'pending_employer', label: 'Pending Employer', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    { value: 'pending_applicant_information', label: 'Pending Applicant Information', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { value: 'under_review', label: 'Under Review', color: 'bg-blue-100 text-brand border-blue-200' },
    { value: 'shared_with_employer', label: 'Shared with Employer', color: 'bg-blue-100 text-brand border-blue-200' },
    { value: 'shortlisted', label: 'Shortlisted', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    { value: 'interviewing', label: 'Interviewing', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    { value: 'interview_invitation_sent', label: 'Interview Invitation Sent', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-teal-100 text-teal-700 border-teal-200' },
    { value: 'interview_completed', label: 'Interview Completed', color: 'bg-sky-100 text-sky-700 border-sky-200' },
    { value: 'approved', label: 'Approved / Hired', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { value: 'offer_sent', label: 'Offer Sent', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { value: 'hired', label: 'Hired', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200' },
    { value: 'offer_declined', label: 'Offer Declined', color: 'bg-rose-100 text-rose-700 border-rose-200' },
    { value: 'closed', label: 'Closed / Archived', color: 'bg-slate-100 text-slate-500 border-slate-200' },
  ];

  function MatchStatusBadge({ status }: { status: string }) {
    const s = MATCH_STATUSES.find(st => st.value === status);
    if (!s) return <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-500">{status}</span>;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-lg border ${s.color}`}>{s.label}</span>;
  }

  // Combined statuses for the Interests table — handles both app and match statuses
  const ALL_INTEREST_STATUSES = [
    ...APPLICATION_STATUSES,
    ...MATCH_STATUSES.filter(s => !APPLICATION_STATUSES.find(as => as.value === s.value)),
  ];

  return (
    <div className="h-full overflow-y-auto p-6 animate-fade-in">
      {/* Toast */}
      {/* Toasts render via the global root-level notification layer. */}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {viewMode === 'jobs' ? <Briefcase className="h-6 w-6 text-brand" /> : <UserCheck className="h-6 w-6 text-amber-600" />}
          <h1 className="text-xl font-black text-slate-900 tracking-tight">
            {viewMode === 'jobs' ? 'Job Board' : 'Interests & Matches'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-slate-100 rounded-xl p-0.5 mr-2">
            <button onClick={() => setViewMode('jobs')}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                viewMode === 'jobs' ? 'bg-white text-brand shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              <Briefcase className="h-3.5 w-3.5" /> Jobs
            </button>
            <button onClick={() => { setViewMode('interests'); if (allMatches.length === 0) fetchAllMatches(); }}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                viewMode === 'interests' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              <UserCheck className="h-3.5 w-3.5" /> Interests
            </button>
          </div>
          {viewMode === 'jobs' && (
            <>
              {canEdit && (
                <>
                  <button onClick={() => { setAiPrompt(''); setShowAiModal(true); }} className="px-4 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark transition-all cursor-pointer flex items-center gap-1.5 shadow-xs">
                    <Sparkles className="h-3.5 w-3.5" /> AI Create
                  </button>
                  <button onClick={() => { resetForm(); setShowCreateModal(true); }} className="px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl  transition-all cursor-pointer flex items-center gap-1.5 shadow-lg shadow-blue-600/20">
                    <Plus className="h-3.5 w-3.5" /> Create Job
                  </button>
                  <button onClick={() => setShowImportModal(true)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 transition-all cursor-pointer flex items-center gap-1.5">
                    <Upload className="h-3.5 w-3.5" /> Import
                  </button>
                </>
              )}
              {canEdit && (
                <button onClick={exportCSV} disabled={jobs.length === 0}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer flex items-center gap-1.5">
                  <Download className="h-3.5 w-3.5" /> Export CSV
                </button>
              )}
            </>
          )}
          <button aria-label="Refresh" title="Refresh" onClick={viewMode === 'jobs' ? fetchJobs : fetchAllMatches} className="p-2 rounded-xl hover:bg-slate-100 transition-all cursor-pointer text-slate-400">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {viewMode === 'jobs' ? (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search jobs..." className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-blue-400" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
              <option value="all">All Statuses</option>
              {JOB_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button onClick={() => setStatusFilter(statusFilter === 'pending_review' ? 'all' : 'pending_review')}
              className={`px-3 py-2 text-xs font-semibold rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 ${
                statusFilter === 'pending_review'
                  ? 'bg-amber-100 border-amber-300 text-amber-800'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300 hover:text-amber-700'
              }`}>
              <Clock className="h-3.5 w-3.5" /> Needs Review
            </button>
            <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 max-w-[180px]">
              <option value="all">All Companies</option>
              {uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 max-w-[160px]">
              <option value="all">All Suppliers</option>
              {uniqueSupplierCodes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title-asc">Title A-Z</option>
              <option value="title-desc">Title Z-A</option>
              <option value="status-asc">Status A-Z</option>
              <option value="status-desc">Status Z-A</option>
            </select>
            {displayPrograms.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                {displayPrograms.map(p => (
                  <button key={p} onClick={() => setProgramFilter(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg border transition-all cursor-pointer ${
                      programFilter.includes(p) ? 'btn-primary text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                    }`}>
                    {p}
                  </button>
                ))}
              </div>
            )}
            {programsLoading && <div className="text-[11px] text-slate-500 italic">Loading programs...</div>}
            <span className="text-xs text-slate-500 font-medium ml-auto whitespace-nowrap">{filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Jobs List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="premium-loader"></div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-20">
              <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-sm text-slate-500 font-medium">No jobs found</p>
              <button onClick={() => { resetForm(); setShowCreateModal(true); }} className="mt-4 px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl  transition-all cursor-pointer">
                Create your first job
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] text-slate-500">
                    Showing {Math.min(filteredJobs.length, page * PAGE_SIZE)} of {filteredJobs.length} jobs
                  </p>
                  <button onClick={selectAllPage}
                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 hover:underline transition-all cursor-pointer">
                    Select All
                  </button>
                </div>
                {filteredJobs.length > PAGE_SIZE && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                      className="px-2 py-1 text-[10px] font-bold text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-30 transition-all cursor-pointer">← Prev</button>
                    <span className="text-[11px] text-slate-500 font-medium px-2">{page}/{Math.ceil(filteredJobs.length / PAGE_SIZE)}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= filteredJobs.length}
                      className="px-2 py-1 text-[10px] font-bold text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-30 transition-all cursor-pointer">Next →</button>
                  </div>
                )}
              </div>
              {selectedIds.size > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-center gap-3 mb-3 animate-fade-in">
                  <span className="text-xs font-bold text-indigo-700">{selectedIds.size} selected</span>
                  <div className="h-4 w-px bg-indigo-200" />
                  <button onClick={() => setBulkModal({ type: 'visibility', visible: true })}
                    className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-[10px] font-bold rounded-lg hover:bg-indigo-100 transition-all cursor-pointer">Show on Board</button>
                  <button onClick={() => setBulkModal({ type: 'visibility', visible: false })}
                    className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-[10px] font-bold rounded-lg hover:bg-indigo-100 transition-all cursor-pointer">Hide from Board</button>
                  <button onClick={() => setBulkModal({ type: 'status', status: 'active' })}
                    className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-[10px] font-bold rounded-lg hover:bg-indigo-100 transition-all cursor-pointer">Set Active</button>
                  <button onClick={() => setBulkModal({ type: 'status', status: 'closed' })}
                    className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-[10px] font-bold rounded-lg hover:bg-indigo-100 transition-all cursor-pointer">Close</button>
                  <button onClick={() => setBulkModal({ type: 'company' })}
                    className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-[10px] font-bold rounded-lg hover:bg-indigo-100 transition-all cursor-pointer">Reassign Company</button>
                  <button onClick={() => { setBulkSupplierCode(''); setBulkModal({ type: 'supplier' }); }}
                    className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-[10px] font-bold rounded-lg hover:bg-indigo-100 transition-all cursor-pointer">Supplier</button>
                  <div className="h-4 w-px bg-indigo-200" />
                  <button onClick={() => setBulkModal({ type: 'archive' })}
                    className="px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold rounded-lg hover:bg-amber-100 transition-all cursor-pointer">Archive</button>
                  <button onClick={() => setBulkModal({ type: 'delete' })}
                    className="px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 text-[10px] font-bold rounded-lg hover:bg-red-100 transition-all cursor-pointer">Delete</button>
                  <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs text-indigo-500 hover:text-indigo-700 font-medium cursor-pointer">Clear</button>
                </div>
              )}
              <div className="space-y-2">
              {filteredJobs.slice(0, page * PAGE_SIZE).map((job: any) => (
                <div key={job.id} className={`glass-panel rounded-xl p-4 hover:shadow-md transition-all relative hover:z-10 ${selectedIds.has(job.id) ? 'ring-2 ring-indigo-400 bg-indigo-50/30' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <input type="checkbox" checked={selectedIds.has(job.id)} onChange={() => toggleSelect(job.id)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-brand-primary cursor-pointer" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {job.imageUrl && (
                          <img src={job.imageUrl} alt=""
                            className="h-7 w-10 rounded-lg border border-slate-200 object-cover shrink-0"
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        <h3 className="text-sm font-bold text-slate-900 truncate">{job.title}</h3>
                        <StatusBadge status={job.status} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Building className="h-3 w-3" />{job.employerName}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>
                        <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{job.jobType}</span>
                        {job.positions && <span>{job.positions} position{job.positions > 1 ? 's' : ''}</span>}
                        {job.supplierCode && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200">SC: {job.supplierCode}</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-[10px]">
                        <span className="flex items-center gap-1 text-brand font-medium">
                          <Users className="h-3 w-3" /> {job.applicantCount || 0} applicant{(job.applicantCount || 0) !== 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1 text-emerald-600 font-medium">
                          <CheckCircle className="h-3 w-3" /> {job.assignedCount || 0} assigned
                        </span>
                        <span className="flex items-center gap-1 text-amber-600 font-medium">
                          <Clock className="h-3 w-3" /> {job.remainingSpots ?? job.positions ?? 1} remaining
                        </span>
                        <label className="flex items-center gap-1.5 cursor-pointer ml-2" onClick={e => e.stopPropagation()}>
                          <span className={`text-[10px] font-medium ${job.visible === true ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {job.visible === true ? 'Visible' : 'Hidden'}
                          </span>
                          <button onClick={() => handleVisibilityToggle(job.id, job.visible !== true)}
                            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${job.visible === true ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${job.visible === true ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                          </button>
                        </label>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-4 shrink-0">
                      {job.status === 'pending_review' && (
                        <>
                          <button onClick={() => handleStatusChange(job.id, 'published')}
                            className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1"
                            title="Approve and publish this job">
                            <Check className="h-3.5 w-3.5" /> Approve
                          </button>
                          <button onClick={() => handleStatusChange(job.id, 'closed')}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1"
                            title="Reject and close this job">
                            <X className="h-3.5 w-3.5" /> Reject
                          </button>
                        </>
                      )}
                      <button onClick={() => viewApplications(job)} className="p-2 rounded-lg bg-brand-active text-brand transition-all cursor-pointer" title="View Applications">
                        <Users className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleEdit(job)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-all cursor-pointer" title="Edit">
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <div
                        className="relative"
                        data-job-status-menu
                        onKeyDown={(e) => {
                          if (e.key === 'Escape' && statusMenuJobId === job.id) {
                            setStatusMenuJobId(null);
                            (e.currentTarget as HTMLElement).querySelector('button')?.focus();
                          }
                        }}
                      >
                        <button
                          onClick={() => setStatusMenuJobId(prev => (prev === job.id ? null : job.id))}
                          aria-haspopup="menu"
                          aria-expanded={statusMenuJobId === job.id}
                          aria-label="Change status"
                          className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-all cursor-pointer" title="Change Status">
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        {statusMenuJobId === job.id && (
                          <div role="menu" aria-label="Change status" className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-200 py-1 z-[var(--z-dropdown)] min-w-[160px]">
                            {JOB_STATUSES.map(s => (
                              <button key={s.value} role="menuitem" onClick={() => { setStatusMenuJobId(null); handleStatusChange(job.id, s.value); }}
                                className={`w-full text-left px-3 py-1.5 text-xs font-medium hover:bg-slate-50 transition-all cursor-pointer ${s.value === job.status ? 'text-brand' : 'text-slate-600'}`}>
                                {s.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button onClick={() => handleDelete(job.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-400 transition-all cursor-pointer" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </>
      ) : (
        <>
          {/* ── All Interests / Matches View ── */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="text" value={matchSearch} onChange={e => setMatchSearch(e.target.value)}
                placeholder="Search by interest ID, job, applicant, program..."
                className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-blue-400" />
            </div>
            <select value={matchStatusFilter} onChange={e => setMatchStatusFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
              <option value="all">All Statuses</option>
              {ALL_INTEREST_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select value={matchCompanyFilter} onChange={e => setMatchCompanyFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 max-w-[180px]">
              <option value="all">All Companies</option>
              {uniqueMatchCompanies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={matchSupplierFilter} onChange={e => setMatchSupplierFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 max-w-[160px]">
              <option value="all">All Suppliers</option>
              {uniqueMatchSupplierCodes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={matchSharedFilter} onChange={e => setMatchSharedFilter(e.target.value as any)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 max-w-[140px]">
              <option value="all">All Shared</option>
              <option value="shared">Shared</option>
              <option value="not_shared">Not Shared</option>
            </select>
            <button onClick={() => setMatchSharedFilter(matchSharedFilter === 'not_shared' ? 'all' : 'not_shared')}
              className={`px-3 py-2 text-xs font-semibold rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 ${
                matchSharedFilter === 'not_shared'
                  ? 'bg-blue-100 border-blue-300 text-blue-800'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700'
              }`}>
              <Share2 className="h-3.5 w-3.5" /> Needs Sharing
            </button>
            {uniqueMatchPrograms.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                {uniqueMatchPrograms.map(p => (
                  <button key={p} onClick={() => setMatchProgramFilter(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg border transition-all cursor-pointer ${
                      matchProgramFilter.includes(p) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
                    }`}>
                    {p}
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => { setGroupByApplicant(!groupByApplicant); setSelectedMatchIds(new Set()); }}
              className={`px-2.5 py-1.5 text-[10px] font-bold rounded-lg border transition-all cursor-pointer flex items-center gap-1 ${
                groupByApplicant
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
              }`}>
              <Users className="h-3 w-3" /> {groupByApplicant ? 'Grouped' : 'Group by Applicant'}
            </button>
            <span className="text-xs text-slate-500 font-medium whitespace-nowrap">
              {groupByApplicant
                ? `${groupedMatches?.length || 0} applicant${(groupedMatches?.length || 0) !== 1 ? 's' : ''}`
                : `${filteredMatches.length} interest${filteredMatches.length !== 1 ? 's' : ''}`
              }
            </span>
            {selectedMatchIds.size > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-brand bg-blue-50 px-2 py-1 rounded-lg">{selectedMatchIds.size} selected</span>
                <button onClick={handleBulkArchive} disabled={bulkActionLoading}
                  className="px-3 py-2 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5">
                  <Archive className="h-3.5 w-3.5" /> Archive
                </button>
                <button onClick={handleBulkShareWithEmployer} disabled={bulkActionLoading}
                  className="px-3 py-2 text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5">
                  <Send className="h-3.5 w-3.5" /> Share with Employer
                </button>
                <button onClick={() => setShowBulkStatusModal(true)} disabled={bulkActionLoading}
                  className="px-3 py-2 text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" /> Update Status
                </button>
              </div>
            )}
            <button onClick={exportMatchesCSV}
              className="px-3 py-2 text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all cursor-pointer flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>

          {matchesLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="premium-loader"></div>
            </div>
          ) : filteredMatches.length === 0 ? (
            <div className="text-center py-20">
              <UserCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-sm text-slate-500 font-medium">No interests or matches found</p>
              <p className="text-xs text-slate-500 mt-1">Interests appear when applicants express interest or admins assign matches to jobs.</p>
            </div>
          ) : groupByApplicant ? (
            /* ── Grouped by Applicant View ── */
            <div className="space-y-3">
              {paginatedMatches.map((group: any) => (
                <div key={group.applicantId} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {group.profilePhotoUrl ? (
                        <img src={group.profilePhotoUrl} alt="" className="h-7 w-7 rounded-full object-cover border border-slate-200" />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600 shrink-0">
                          {(group.applicantName || '?')[0]}
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-bold text-slate-800">{group.applicantName}</p>
                        <p className="text-[11px] text-slate-500">{group.applicantEmail} · {group.programType} · {group.items.length} interest{group.items.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs whitespace-nowrap">
                      <thead className="bg-white border-b border-slate-100">
                        <tr>
                          <th className="text-left py-2 px-3 text-slate-400 font-bold text-[10px]">Type</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-bold text-[10px]">Date</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-bold text-[10px]">Job Title</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-bold text-[10px]">Company</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-bold text-[10px]">Supplier</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-bold text-[10px]">Status</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-bold text-[10px]">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {group.items.map((m: any) => (
                          <tr key={`${m._type || 'app'}-${m.id}`} className={`hover:bg-amber-50/20 transition-colors ${m._type === 'application' ? 'bg-blue-50/10' : ''}`}>
                            <td className="py-2 px-3">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded ${
                                m._type === 'match' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-brand'
                              }`}>
                                {m._type === 'match' ? 'Match' : 'App'}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-slate-600 text-[10px]">
                              {(m.appliedAt || m.createdAt) ? new Date(m.appliedAt || m.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                            <td className="py-2 px-3 font-semibold text-slate-800 max-w-[180px] truncate" title={m.jobTitle}>{m.jobTitle || '—'}</td>
                            <td className="py-2 px-3 text-slate-600 max-w-[140px] truncate" title={m.employerName}>
                              <span className="flex items-center gap-1"><Building className="h-3 w-3 shrink-0 text-slate-400" />{m.employerName || '—'}</span>
                            </td>
                            <td className="py-2 px-3">
                              {m.supplierCode ? (
                                <span className="inline-flex px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-100 text-purple-700 border border-purple-200">{m.supplierCode}</span>
                              ) : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="py-2 px-3">
                              {m._type === 'match' ? <MatchStatusBadge status={m.status} /> : <AppStatusBadge status={m.status} />}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex items-center gap-1 justify-end">
                                {onViewApplicantProfile ? (
                                  <button onClick={async () => {
                                    setProfileLoading(m.id);
                                    try {
                                      const type = m._type || 'application';
                                      const data = await safeFetch(`/api/admin/applications/${m.id}/full?type=${type}`);
                                      if (data.application) onViewApplicantProfile(data.application);
                                    } catch (err: any) { showToast(err.message, 'error'); }
                                    finally { setProfileLoading(null); }
                                  }} disabled={profileLoading === m.id}
                                    className="px-2 py-1 text-[10px] font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all cursor-pointer flex items-center gap-1 disabled:opacity-50">
                                    {profileLoading === m.id ? <span className="animate-spin h-3 w-3 border-2 border-indigo-600 border-t-transparent rounded-full" /> : <Eye className="h-3 w-3" />}
                                    View
                                  </button>
                                ) : (
                                  <button onClick={() => setPreviewApp(m)} className="px-2 py-1 text-[10px] font-bold text-brand bg-brand-active rounded-lg transition-all cursor-pointer flex items-center gap-1">
                                    <Eye className="h-3 w-3" /> View
                                  </button>
                                )}
                                {m._type === 'match' ? (
                                  <select value={m.status} onChange={e => handleMatchStatusChange(m, e.target.value)}
                                    className="px-2 py-1 text-[10px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                                    {MATCH_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                  </select>
                                ) : (
                                  <select value={m.status} onChange={e => handleInterestStatusChange(m, e.target.value)}
                                    className="px-2 py-1 text-[10px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                                    {APPLICATION_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                  </select>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ── Flat List View ── */
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs whitespace-nowrap">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3 w-8">
                        <input type="checkbox" checked={selectedMatchIds.size === filteredMatches.length && filteredMatches.length > 0}
                          onChange={toggleSelectAllMatches}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-brand focus:ring-brand-primary/20 cursor-pointer" />
                      </th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">ID</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Type</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Date</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Job Title</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Company</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Supplier</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Location</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Applicant</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Program</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Status</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-bold">Shared</th>
                      <th className="text-right py-2.5 px-3 text-slate-500 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMatches.map((m: any) => (
                      <tr key={`${m._type || 'app'}-${m.id}`} className={`border-b border-slate-100 hover:bg-amber-50/30 transition-colors ${m._type === 'application' ? 'bg-blue-50/20' : ''} ${selectedMatchIds.has(m.id) ? 'bg-amber-50/60' : ''}`}>
                        <td className="py-2.5 px-3">
                          <input type="checkbox" checked={selectedMatchIds.has(m.id)}
                            onChange={() => toggleMatchSelect(m.id)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-brand focus:ring-brand-primary/20 cursor-pointer" />
                        </td>
                        <td className="py-2.5 px-3 font-mono text-[11px] text-slate-500">{m.applicationId || m.id?.slice(0, 10) || '—'}</td>
                        <td className="py-2.5 px-3">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded ${
                            m._type === 'match' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-brand'
                          }`}>
                            {m._type === 'match' ? 'Match' : 'App'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 text-[10px] whitespace-nowrap">
                          {(m.appliedAt || m.createdAt) ? new Date(m.appliedAt || m.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="py-2.5 px-3 font-semibold text-slate-800 max-w-[200px] truncate" title={m.jobTitle}>{m.jobTitle || '—'}</td>
                        <td className="py-2.5 px-3 text-slate-600 max-w-[150px] truncate" title={m.employerName}>
                          <span className="flex items-center gap-1"><Building className="h-3 w-3 shrink-0 text-slate-400" />{m.employerName || '—'}</span>
                        </td>
                        <td className="py-2.5 px-3">
                          {m.supplierCode ? (
                            <span className="inline-flex px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-100 text-purple-700 border border-purple-200">{m.supplierCode}</span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-2.5 px-3 text-slate-500">{m.jobLocation || '—'}</td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2">
                            {m.profilePhotoUrl ? (
                              <img src={m.profilePhotoUrl} alt="" className="h-6 w-6 rounded-full object-cover border border-slate-200" />
                            ) : (
                              <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-bold text-slate-400 shrink-0">
                                {(m.applicantName || '?')[0]}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800 truncate max-w-[140px]" title={m.applicantName}>{m.applicantName}</p>
                              {m.applicantEmail && <p className="text-[11px] text-slate-500 truncate max-w-[140px]">{m.applicantEmail}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="inline-flex px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
                            {m.programType || '—'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          {m._type === 'match' ? <MatchStatusBadge status={m.status} /> : <AppStatusBadge status={m.status} />}
                        </td>
                        <td className="py-2.5 px-3">
                          {m.sharedWithEmployer ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-50 text-emerald-700 border border-emerald-100"><CheckCircle className="h-3 w-3" /> Shared</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-slate-50 text-slate-500 border border-slate-200"><Clock className="h-3 w-3" /> Not Shared</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {onViewApplicantProfile ? (
                              <button onClick={async () => {
                                setProfileLoading(m.id);
                                try {
                                  const type = m._type || 'application';
                                  const data = await safeFetch(`/api/admin/applications/${m.id}/full?type=${type}`);
                                  if (data.application) onViewApplicantProfile(data.application);
                                } catch (err: any) { showToast(err.message, 'error'); }
                                finally { setProfileLoading(null); }
                              }} disabled={profileLoading === m.id}
                                className="px-2 py-1 text-[10px] font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all cursor-pointer flex items-center gap-1 disabled:opacity-50">
                                {profileLoading === m.id ? <span className="animate-spin h-3 w-3 border-2 border-indigo-600 border-t-transparent rounded-full" /> : <Eye className="h-3 w-3" />}
                                View
                              </button>
                            ) : (
                              <button onClick={() => setPreviewApp(m)} className="px-2 py-1 text-[10px] font-bold text-brand bg-brand-active rounded-lg transition-all cursor-pointer flex items-center gap-1">
                                <Eye className="h-3 w-3" /> View
                              </button>
                            )}
                            {m._type === 'match' ? (
                              <select value={m.status} onChange={e => handleMatchStatusChange(m, e.target.value)}
                                className="px-2 py-1 text-[10px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                                {MATCH_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                              </select>
                            ) : (
                              <select value={m.status} onChange={e => handleInterestStatusChange(m, e.target.value)}
                                className="px-2 py-1 text-[10px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                                {APPLICATION_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                              </select>
                            )}
                            {!m.sharedWithEmployer && (
                              <button onClick={() => handleShareWithEmployer(m)}
                                className="px-2 py-1 text-[10px] font-bold text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer flex items-center gap-1"
                              >
                                <Send className="h-3 w-3" /> Share
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Pagination Controls ── */}
          {!matchesLoading && filteredMatches.length > 0 && (
            <div className="flex items-center justify-between pt-3 pb-1">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500 font-medium">Rows per page:</span>
                <select value={matchPageSize} onChange={e => { setMatchPageSize(Number(e.target.value)); setMatchPage(1); }}
                  className="px-2 py-1 text-[10px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 cursor-pointer">
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <span className="text-[11px] text-slate-500 font-medium ml-2">
                  {groupByApplicant
                    ? `${(matchPage - 1) * matchPageSize + 1}–${Math.min(matchPage * matchPageSize, groupedMatches?.length || 0)} of ${groupedMatches?.length || 0}`
                    : `${(matchPage - 1) * matchPageSize + 1}–${Math.min(matchPage * matchPageSize, filteredMatches.length)} of ${filteredMatches.length}`
                  }
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button aria-label="Previous page" title="Previous page" onClick={() => setMatchPage(p => Math.max(1, p - 1))} disabled={matchPage <= 1}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:text-slate-300 disabled:cursor-not-allowed transition-all cursor-pointer">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="text-[10px] font-bold text-slate-600 px-2">{matchPage} / {totalPages || 1}</span>
                <button aria-label="Next page" title="Next page" onClick={() => setMatchPage(p => Math.min(totalPages, p + 1))} disabled={matchPage >= totalPages}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:text-slate-300 disabled:cursor-not-allowed transition-all cursor-pointer">
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <Modal onClose={() => { setShowCreateModal(false); resetForm(); }} ariaLabel={editingJob ? 'Edit Job' : 'Create Job'}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto my-4" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-sm sm:text-base font-black text-slate-900">{editingJob ? 'Edit Job' : 'Create Job'}</h2>
              <button aria-label="Close" title="Close" onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Job Title *</label>
                  <input type="text" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div ref={empRef} className="relative">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Company *</label>
                  <input type="text" required value={form.employerName}
                    onFocus={() => { fetchEmployers(); setShowEmpDropdown(true); }}
                    onChange={e => { setForm(f => ({ ...f, employerName: e.target.value, employerId: '' })); setEmpSearch(e.target.value); setShowEmpDropdown(true); }}
                    placeholder="Search companies..."
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                  {showEmpDropdown && (
                    <div className="absolute z-[8500] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                      {employers
                        .filter(e => e.companyName.toLowerCase().includes(empSearch.toLowerCase() || ''))
                        .map(e => (
                          <button key={e.id} type="button" onClick={() => { setForm(f => ({ ...f, employerId: e.id, employerName: e.companyName })); setShowEmpDropdown(false); setEmpSearch(''); }}
                            className="w-full text-left px-3 py-2 text-xs font-medium bg-brand-active text-slate-700 transition-colors cursor-pointer flex items-center gap-2">
                            <Building className="h-3 w-3 text-slate-400" />
                            {e.companyName}
                          </button>
                        ))}
                      {employers.length === 0 && (
                        <div className="px-3 py-3 text-xs text-slate-500 text-center">Loading companies...</div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Location *</label>
                  <input type="text" required value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Job Type *</label>
                  <select value={form.jobType} onChange={e => setForm(f => ({ ...f, jobType: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                    {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Positions</label>
                  <input type="number" min="1" value={form.positions} onChange={e => setForm(f => ({ ...f, positions: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Description *</label>
                  <div className="relative">
                    <textarea rows={4} required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                    <div className="absolute top-1 right-1">
                      <ImproveTextDropdown
                        getValue={() => form.description}
                        onImprove={(v) => setForm(f => ({ ...f, description: v }))}
                        isHtml={false}
                        context="job description"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Requirements</label>
                  <div className="relative">
                    <textarea rows={3} value={form.requirements} onChange={e => setForm(f => ({ ...f, requirements: e.target.value }))}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                    <div className="absolute top-1 right-1">
                      <ImproveTextDropdown
                        getValue={() => form.requirements}
                        onImprove={(v) => setForm(f => ({ ...f, requirements: v }))}
                        isHtml={false}
                        context="job requirements"
                      />
                    </div>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Job Image URL</label>
                  <div className="flex items-center gap-2">
                    <input type="url" value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                      placeholder="https://example.com/company-logo.jpg"
                      className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                    <button aria-label="Remove image" title="Remove image" type="button" onClick={() => setForm(f => ({ ...f, imageUrl: '' }))}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-all cursor-pointer">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {form.imageUrl && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={form.imageUrl} alt="Preview"
                        className="h-10 w-16 object-cover rounded-lg border border-slate-200"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      <span className="text-[11px] text-slate-500 truncate max-w-[300px]">{form.imageUrl}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Salary</label>
                  <input type="text" value={form.salary} onChange={e => setForm(f => ({ ...f, salary: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Compensation</label>
                  <input type="text" value={form.compensation} onChange={e => setForm(f => ({ ...f, compensation: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Country</label>
                  <input type="text" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Programs * (select all that apply)</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(programs.length > 0 ? programs : ['J1 Internship', 'J1 Trainee', 'J1 Summer Work Travel', 'J1 Teacher', 'J1 Au Pair', 'J1 Camp Counselor', 'H2B', 'F1', 'Other']).map(p => {
                      const selected = form.programs.includes(p);
                      return (
                        <button key={p} type="button" onClick={() => setForm(f => ({
                          ...f,
                          programs: selected ? f.programs.filter(x => x !== p) : [...f.programs, p],
                          programType: selected ? (f.programs.length > 1 ? f.programs.find(x => x !== p) || '' : '') : p,
                        }))}
                          className={`px-3 py-1.5 text-[11px] font-bold rounded-lg border transition-all cursor-pointer ${
                            selected
                              ? 'btn-primary text-white border-brand shadow-sm'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                          }`}>
                          {selected && <CheckCircle className="h-3 w-3 inline mr-1 -mt-0.5" />}
                          {p}
                        </button>
                      );
                    })}
                  </div>
                  {programsLoading && <p className="text-[11px] text-slate-500 mt-1 italic">Loading programs from database...</p>}
                  <p className="text-[11px] text-slate-500 mt-1">Applicants can only apply if their program matches one of the selected programs.</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Industry</label>
                  <input type="text" value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Supplier Code</label>
                  <input type="text" value={form.supplierCode || ''} onChange={e => setForm(f => ({ ...f, supplierCode: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" placeholder="e.g. SUP-001" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Application Deadline</label>
                  <input type="date" value={form.applicationDeadline} onChange={e => setForm(f => ({ ...f, applicationDeadline: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Tags (comma-separated)</label>
                  <input type="text" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" placeholder="e.g. hospitality, summer, urgent" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                <button type="submit" className="px-6 py-2 btn-primary text-white text-xs font-bold rounded-xl  transition-all cursor-pointer shadow-lg shadow-blue-600/20">
                  {editingJob ? 'Update Job' : 'Create Job'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <Modal onClose={resetImportModal} ariaLabel="Import Jobs">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-black text-slate-900">Import Jobs</h2>
                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                  {importStep === 'upload' ? 'Step 1/2' : 'Step 2/2'}
                </span>
              </div>
              <button aria-label="Close" title="Close" onClick={resetImportModal} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>

            <div className="p-6">
              {/* ── Step 1: Upload ── */}
              {importStep === 'upload' && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500">Upload a CSV file with job data. The first row must contain column headers.</p>
                  <label
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                    onDragEnter={e => { e.preventDefault(); e.stopPropagation(); dragCounter.current++; setDragOver(true); }}
                    onDragLeave={e => { e.preventDefault(); e.stopPropagation(); dragCounter.current--; if (dragCounter.current <= 0) { dragCounter.current = 0; setDragOver(false); } }}
                    onDrop={e => { e.preventDefault(); e.stopPropagation(); setDragOver(false); dragCounter.current = 0; handleFileSelect(e.dataTransfer.files[0]); }}
                    className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer ${dragOver ? 'border-brand bg-blue-50 scale-[1.01] shadow-lg' : 'border-slate-300  bg-brand-active/30'}`}>
                    <Upload className={`h-8 w-8 mb-2 ${dragOver ? 'text-blue-500' : 'text-slate-300'}`} />
                    <span className="text-sm font-bold text-slate-600">{dragOver ? 'Drop file here' : 'Click to select CSV file'}</span>
                    <span className="text-xs text-slate-500 mt-1">or drag and drop</span>
                    <input type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={e => handleFileSelect(e.target.files?.[0])} />
                  </label>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={resetImportModal} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                  </div>
                </div>
              )}

              {/* ── Step 2: Field Mapping ── */}
              {importStep === 'mapping' && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500">
                    Map each CSV column to the corresponding job field. The system automatically detected column mappings — adjust as needed.
                    <strong className="block mt-1 text-amber-600">* Title must be mapped</strong>
                  </p>

                  {/* CSV Preview */}
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50">
                          {csvHeaders.map(h => <th key={h} className="px-3 py-2 text-left font-bold text-slate-600 whitespace-nowrap">{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.map((row, ri) => (
                          <tr key={ri} className="border-t border-slate-100">
                            {csvHeaders.map(h => <td key={h} className="px-3 py-1.5 text-slate-700 truncate max-w-[160px]">{row[h]}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvRows.length - 1 > 5 && (
                      <p className="text-[11px] text-slate-500 px-3 py-1.5 border-t border-slate-100 bg-slate-50/50">
                        Showing 5 of {csvRows.length - 1} rows
                      </p>
                    )}
                  </div>

                  {/* Field Mapping */}
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-600">Column Mappings</p>
                    {csvHeaders.map((header, idx) => {
                      const mapping = fieldMapping.find(m => m.csvColumn === header);
                      return (
                        <div key={header} className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-600 w-36 shrink-0 truncate" title={header}>{header}</span>
                          <span className="text-slate-300 shrink-0">→</span>
                          <select
                            value={mapping?.jobField || '_skip'}
                            onChange={e => {
                              const newVal = e.target.value;
                              setFieldMapping(prev => prev.map(m => m.csvColumn === header ? { ...m, jobField: newVal } : m));
                            }}
                            className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                          >
                            {JOB_FIELDS.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                          {/* Show a sample value from first row */}
                          <span className="text-[11px] text-slate-500 w-28 truncate text-right" title={csvPreview[0]?.[header] || ''}>
                            {csvPreview[0]?.[header] ? `e.g. ${csvPreview[0][header].slice(0, 20)}` : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* ── Company override (optional — uses raw CSV company name if omitted) ── */}
                  <div className="border-t border-slate-200 pt-4">
                    <p className="text-xs font-bold text-slate-600 mb-2">Override Company Name <span className="text-slate-400 font-normal">(optional)</span></p>
                    <p className="text-[11px] text-slate-500 mb-2">Leave blank to use the company name from each row's CSV data. Jobs don't need to be linked to a company record.</p>
                    <div ref={importEmployerRef} className="relative">
                      <input type="text" placeholder="Type a company name or search existing..." value={importEmployerSearch}
                        onFocus={() => { fetchEmployers(); setShowImportEmployerDropdown(true); }}
                        onChange={e => {
                          setImportEmployerSearch(e.target.value);
                          setImportEmployerName(e.target.value);
                          setImportEmployerId('');
                          setShowImportEmployerDropdown(true);
                        }}
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                      {showImportEmployerDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto">
                          {employers.filter(e => e.companyName.toLowerCase().includes(importEmployerSearch.toLowerCase())).map(e => (
                            <button key={e.id} type="button" onClick={() => {
                              setImportEmployerId(e.id);
                              setImportEmployerName(e.companyName);
                              setImportEmployerSearch(e.companyName);
                              setShowImportEmployerDropdown(false);
                            }} className="w-full text-left px-3 py-2 text-xs font-medium text-slate-700 bg-brand-active transition-all cursor-pointer">
                              {e.companyName}
                            </button>
                          ))}
                          {employers.filter(e => e.companyName.toLowerCase().includes(importEmployerSearch.toLowerCase())).length === 0 && !!importEmployerSearch && (
                            <div className="px-3 py-3 text-xs text-slate-500 text-center italic">
                              Using "<strong>{importEmployerSearch}</strong>" as raw company name
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {importEmployerName && (
                      <p className="text-[11px] text-emerald-600 font-semibold mt-1">
                        {importEmployerId ? '🔗 Linked: ' : '📝 Text: '}{importEmployerName}
                      </p>
                    )}
                  </div>

                  {/* ── Programs multi-select (applied to all imported jobs) ── */}
                  <div className="border-t border-slate-200 pt-4">
                    <p className="text-xs font-bold text-slate-600 mb-2">Assign Programs (all imported jobs)</p>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {programs.map(p => {
                        const selected = importPrograms.includes(p);
                        return (
                          <button key={p} type="button" onClick={() => {
                            setImportPrograms(prev => selected ? prev.filter(x => x !== p) : [...prev, p]);
                          }} className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg border transition-all cursor-pointer ${selected ? 'btn-primary text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                            {p}
                          </button>
                        );
                      })}
                    </div>
                    {importPrograms.length > 0 && (
                      <p className="text-[11px] text-brand font-semibold">{importPrograms.length} program(s) selected</p>
                    )}
                  </div>

                  {/* Update existing toggle */}
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <input type="checkbox" id="updateExisting" checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)}
                      className="rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                    <label htmlFor="updateExisting" className="text-xs font-semibold text-amber-800 cursor-pointer">
                      Update existing jobs if <strong>Job ID</strong> is mapped and matches
                    </label>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setImportStep('upload')} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Back</button>
                    <button onClick={handleImportFromMapping} disabled={importing}
                      className="px-6 py-2 btn-primary text-white text-xs font-bold rounded-xl  disabled:opacity-50 transition-all cursor-pointer shadow-lg shadow-blue-600/20">
                      {importing ? 'Importing...' : `Import ${csvRows.length - 1 || 0} Job${csvRows.length - 1 !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Action Confirmation Modal */}
      {bulkModal && (
        <Modal onClose={() => setBulkModal(null)} ariaLabel="Confirm Bulk Action">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-sm font-black text-slate-900">Confirm Bulk Action</h2>
              <button aria-label="Close" title="Close" onClick={() => setBulkModal(null)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {bulkModal.type === 'visibility' && (
                <>
                  <div className="bg-amber-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-amber-700 font-medium">
                      {bulkModal.visible ? 'Show' : 'Hide'} <strong>{selectedIds.size} job{selectedIds.size !== 1 ? 's' : ''}</strong> on the applicant job board.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setBulkModal(null)}
                      className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                    <button onClick={() => executeBulkVisibility(bulkModal.visible!)}
                      className="px-6 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark transition-all cursor-pointer shadow-lg shadow-brand-primary/20">
                      Confirm
                    </button>
                  </div>
                </>
              )}
              {bulkModal.type === 'status' && (
                <>
                  <div className="bg-amber-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-amber-700 font-medium">
                      Set <strong>{selectedIds.size} job{selectedIds.size !== 1 ? 's' : ''}</strong> to status <strong>"{bulkModal.status}"</strong>.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setBulkModal(null)}
                      className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                    <button onClick={() => executeBulkStatus(bulkModal.status!)}
                      className="px-6 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark transition-all cursor-pointer shadow-lg shadow-brand-primary/20">
                      Confirm
                    </button>
                  </div>
                </>
              )}
              {bulkModal.type === 'company' && (
                <>
                  <div className="bg-amber-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-amber-700 font-medium">
                      Reassign <strong>{selectedIds.size} job{selectedIds.size !== 1 ? 's' : ''}</strong> to a different company.
                    </p>
                  </div>
                  <div ref={bulkCompanyRef} className="relative">
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Select Company</label>
                    <input type="text" required placeholder="Search companies..." value={bulkCompanySearch}
                      onFocus={() => { fetchEmployers(); setShowBulkCompanyDropdown(true); }}
                      onChange={e => { setBulkCompanySearch(e.target.value); setBulkCompanyId(''); setBulkCompanyName(''); setShowBulkCompanyDropdown(true); }}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                    {showBulkCompanyDropdown && (
                      <div className="absolute z-[8500] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-36 overflow-y-auto">
                        {employers.filter(e => e.companyName.toLowerCase().includes(bulkCompanySearch.toLowerCase())).map(e => (
                          <button key={e.id} type="button" onClick={() => { setBulkCompanyId(e.id); setBulkCompanyName(e.companyName); setBulkCompanySearch(e.companyName); setShowBulkCompanyDropdown(false); }}
                            className="w-full text-left px-3 py-2 text-xs font-medium hover:bg-indigo-50 text-slate-700 transition-colors cursor-pointer flex items-center gap-2">
                            <Building className="h-3 w-3 text-slate-400" />{e.companyName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setBulkModal(null)}
                      className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                    <button onClick={executeBulkCompany} disabled={!bulkCompanyId}
                      className="px-6 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark disabled:opacity-50 transition-all cursor-pointer shadow-lg shadow-brand-primary/20">
                      Reassign
                    </button>
                  </div>
                </>
              )}
              {bulkModal.type === 'supplier' && (
                <>
                  <div className="bg-amber-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-amber-700 font-medium">
                      Set supplier code for <strong>{selectedIds.size} job{selectedIds.size !== 1 ? 's' : ''}</strong>.
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Supplier Code</label>
                    <input type="text" value={bulkSupplierCode} onChange={e => setBulkSupplierCode(e.target.value)}
                      placeholder="e.g. SC-001" autoFocus
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setBulkModal(null)}
                      className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                    <button onClick={executeBulkSupplier} disabled={!bulkSupplierCode.trim()}
                      className="px-6 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark disabled:opacity-50 transition-all cursor-pointer shadow-lg shadow-brand-primary/20">
                      Update Supplier Code
                    </button>
                  </div>
                </>
              )}
              {bulkModal.type === 'archive' && (
                <>
                  <div className="bg-amber-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-amber-700 font-medium">
                      Archive <strong>{selectedIds.size} job{selectedIds.size !== 1 ? 's' : ''}</strong>? This will move them to the archived status.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setBulkModal(null)}
                      className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                    <button onClick={executeBulkArchive}
                      className="px-6 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700 transition-all cursor-pointer shadow-lg shadow-amber-600/20">
                      Archive Jobs
                    </button>
                  </div>
                </>
              )}
              {bulkModal.type === 'delete' && (
                <>
                  <div className="bg-red-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-red-700 font-medium">
                      Permanently delete <strong>{selectedIds.size} job{selectedIds.size !== 1 ? 's' : ''}</strong>? This action <strong>cannot be undone</strong>.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setBulkModal(null)}
                      className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                    <button onClick={executeBulkDelete}
                      className="px-6 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 transition-all cursor-pointer shadow-lg shadow-red-600/20">
                      Delete {selectedIds.size} Job{selectedIds.size !== 1 ? 's' : ''}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ── Applications Modal ── */}
      {showApplications && selectedJob && (
        <Modal onClose={() => { setShowApplications(false); setSelectedJob(null); setSelectedAppModalIds(new Set()); }} ariaLabel="Applications / Interest">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10 shrink-0">
              <div>
                <h2 className="text-base font-black text-slate-900">Applications / Interest</h2>
                <p className="text-xs text-slate-500">{selectedJob.title} — {selectedJob.employerName}</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => { setShowApplications(false); setSelectedJob(null); setSelectedAppModalIds(new Set()); }} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>

            {/* Toolbar */}
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/50 shrink-0">
              <div className="flex items-center gap-3 flex-wrap">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input type="text" value={appSearch} onChange={e => { setAppSearch(e.target.value); setAppPage(1); }}
                    placeholder="Search by name, email, or ID..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                {/* Status filter */}
                <select value={appStatusFilter} onChange={e => { setAppStatusFilter(e.target.value); setAppPage(1); }}
                  className="px-3 py-1.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                  <option value="all">All Statuses</option>
                  {APPLICATION_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                {/* Type filter */}
                <select value={appTypeFilter} onChange={e => { setAppTypeFilter(e.target.value); setAppPage(1); }}
                  className="px-3 py-1.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                  <option value="all">All Types</option>
                  <option value="application">Applications</option>
                  <option value="match">Matches / Interest</option>
                </select>
                {/* Export */}
                <button onClick={() => {
                  const header = 'Type,Application ID,Primary App ID,First Name,Last Name,Email,Country,Gender,Program,Status,Date Applied,Notes';
                  const rows = filteredApps.map((a: any) => [
                    a._type === 'match' ? 'Match' : 'Application',
                    a.id || '',
                    a.primaryApplicationId || '',
                    a.applicantFirstName || '',
                    a.applicantLastName || '',
                    a.applicantEmail || '',
                    a.country || '',
                    a.gender || '',
                    a.programType || '',
                    a.status || '',
                    a.appliedAt ? new Date(a.appliedAt).toISOString().split('T')[0] : (a.createdAt ? new Date(a.createdAt).toISOString().split('T')[0] : ''),
                    a.adminNotes || a.notes || '',
                  ].map(csvCell).join(','));
                  const blob = new Blob([header + '\n' + rows], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = `applications-${selectedJob.title.replace(/\s+/g, '-')}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }} className="px-3 py-1.5 bg-success text-white text-[10px] font-bold rounded-xl hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] transition-all cursor-pointer flex items-center gap-1 shadow-sm">
                  <Download className="h-3 w-3" /> Export CSV
                </button>
              </div>
              {/* Stats bar */}
              <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                <span>{filteredApps.length} of {applications.length} shown</span>
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Applications ({applications.filter((a: any) => a._type === 'application').length})</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Matches ({applications.filter((a: any) => a._type === 'match').length})</span>
              </div>
              {/* Bulk actions for applications modal */}
              {selectedAppModalIds.size > 0 && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-xs font-bold text-brand bg-blue-50 px-2 py-1 rounded-lg">{selectedAppModalIds.size} selected</span>
                  <button onClick={handleBulkShareApplicationsInModal} disabled={bulkActionLoading}
                    className="px-3 py-2 text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5">
                    <Send className="h-3.5 w-3.5" /> Share with Employer
                  </button>
                  <button onClick={() => setSelectedAppModalIds(new Set())}
                    className="px-3 py-2 text-xs font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-all cursor-pointer flex items-center gap-1.5">
                    <X className="h-3.5 w-3.5" /> Clear Selection
                  </button>
                </div>
              )}
            </div>

            {/* Table body */}
            <div className="flex-1 overflow-y-auto">
              {applications.length === 0 ? (
                <div className="text-center py-10">
                  <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No applications yet</p>
                </div>
              ) : filteredApps.length === 0 ? (
                <div className="text-center py-10">
                  <Search className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">No records match your filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs whitespace-nowrap">
                    <thead className="sticky top-0 bg-slate-50 z-10">
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2.5 px-2 w-8">
                          <input type="checkbox" checked={filteredApps.length > 0 && filteredApps.every(app => selectedAppModalIds.has(`${app._type || 'application'}-${app.id}`))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const all = new Set(selectedAppModalIds);
                                filteredApps.forEach(app => all.add(`${app._type || 'application'}-${app.id}`));
                                setSelectedAppModalIds(all);
                              } else {
                                const cleared = new Set(selectedAppModalIds);
                                filteredApps.forEach(app => cleared.delete(`${app._type || 'application'}-${app.id}`));
                                setSelectedAppModalIds(cleared);
                              }
                            }}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-brand focus:ring-brand-primary/20 cursor-pointer" />
                        </th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold"># </th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Type</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Photo</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">First Name</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Last Name</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Email</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Country</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Gender</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Program</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">App ID</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Status</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Shared</th>
                        <th className="text-left py-2.5 px-2 text-slate-500 font-bold">Date</th>
                        <th className="text-right py-2.5 px-2 text-slate-500 font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApps.slice(0, appPage * APP_PAGE_SIZE).map((app: any, idx: number) => (
                        <tr key={`${app._type}-${app.id}`} className="border-b border-slate-100 bg-brand-active/50 transition-colors">
                          <td className="py-2 px-2">
                            <input type="checkbox" checked={selectedAppModalIds.has(`${app._type || 'application'}-${app.id}`)}
                              onChange={() => toggleAppModalSelect(`${app._type || 'application'}-${app.id}`)}
                              className="h-3.5 w-3.5 rounded border-slate-300 text-brand focus:ring-brand-primary/20 cursor-pointer" />
                          </td>
                          <td className="py-2 px-2 text-slate-400 text-center text-[10px]">{(appPage - 1) * APP_PAGE_SIZE + idx + 1}</td>
                          <td className="py-2 px-2">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded ${
                              app._type === 'match' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-brand'
                            }`}>
                              {app._type === 'match' ? 'Match' : 'App'}
                            </span>
                          </td>
                          <td className="py-2 px-2">
                            {app.profilePhotoUrl ? (
                              <img src={app.profilePhotoUrl} alt="" className="h-7 w-7 rounded-full object-cover border border-slate-200" />
                            ) : (
                              <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-400">
                                {(app.applicantFirstName || app.applicantName || '?')[0]}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-2 font-semibold text-slate-800">{app.applicantFirstName || '—'}</td>
                          <td className="py-2 px-2 font-semibold text-slate-800">{app.applicantLastName || '—'}</td>
                          <td className="py-2 px-2 text-slate-500 max-w-[140px] truncate">
                            {app.applicantEmail || '—'}
                          </td>
                          <td className="py-2 px-2 text-slate-600">{app.country || '—'}</td>
                          <td className="py-2 px-2 text-slate-600">{app.gender || '—'}</td>
                          <td className="py-2 px-2 text-slate-600 max-w-[100px] truncate" title={app.programType}>{app.programType || '—'}</td>
                          <td className="py-2 px-2 font-mono text-[11px] text-slate-500">{app.primaryApplicationId?.slice(0, 10) || app.id?.slice(0, 8) || '—'}</td>
                          <td className="py-2 px-2"><AppStatusBadge status={app.status} /></td>
                          <td className="py-2 px-2">
                            {app.sharedWithEmployer ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-50 text-emerald-700 border border-emerald-100"><CheckCircle className="h-3 w-3" /> Shared</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-slate-50 text-slate-500 border border-slate-200"><Clock className="h-3 w-3" /> Not Shared</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-slate-500 text-[10px] whitespace-nowrap">
                            {app.appliedAt ? new Date(app.appliedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) :
                             app.createdAt ? new Date(app.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <div className="flex items-center gap-1 justify-end">
                              {onViewApplicantProfile && (
                                <button onClick={async () => {
                                  setProfileLoading(app.id);
                                  try {
                                    const data = await safeFetch(`/api/admin/applications/${app.id}/full?type=${app._type || 'application'}`);
                                    if (data.application) onViewApplicantProfile(data.application);
                                  } catch (err: any) { showToast(err.message, 'error'); }
                                  finally { setProfileLoading(null); }
                                }} disabled={profileLoading === app.id}
                                  className="px-2 py-1 text-[10px] font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all cursor-pointer flex items-center gap-1 disabled:opacity-50">
                                  {profileLoading === app.id ? <span className="animate-spin h-3 w-3 border-2 border-indigo-600 border-t-transparent rounded-full" /> : <Eye className="h-3 w-3" />}
                                  View
                                </button>
                              )}
                              {!onViewApplicantProfile && (
                                <button onClick={() => setPreviewApp(app)} className="px-2 py-1 text-[10px] font-bold text-brand bg-brand-active rounded-lg transition-all cursor-pointer flex items-center gap-1">
                                  <Eye className="h-3 w-3" /> View
                                </button>
                              )}
                              <select value={app.status} onChange={e => handleAppStatusChange(app, e.target.value)}
                                className="px-2 py-1 text-[10px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                                {APPLICATION_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                              </select>
                              <button onClick={() => handleRequestInfoInModal(app)}
                                className="px-2 py-1 text-[10px] font-bold text-amber-600 hover:bg-amber-50 rounded-lg transition-all cursor-pointer flex items-center gap-1"
                                title="Request Additional Information">
                                <AlertCircle className="h-3 w-3" /> Request Info
                              </button>
                              {!app.sharedWithEmployer && (
                                <button onClick={() => handleShareApplicationInModal(app)}
                                  className="px-2 py-1 text-[10px] font-bold text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer flex items-center gap-1"
                                >
                                  <Send className="h-3 w-3" /> Share
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer pagination */}
            {filteredApps.length > APP_PAGE_SIZE && (
              <div className="shrink-0 border-t border-slate-200 px-6 py-3 flex items-center justify-between bg-white">
                <p className="text-[11px] text-slate-500">{Math.min(filteredApps.length, appPage * APP_PAGE_SIZE)} of {filteredApps.length} shown</p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setAppPage(p => Math.max(1, p - 1))} disabled={appPage <= 1}
                    className="px-3 py-1.5 text-[10px] font-bold text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-30 transition-all cursor-pointer">← Prev</button>
                  <span className="text-[11px] text-slate-500 font-medium px-2">{appPage}/{Math.ceil(filteredApps.length / APP_PAGE_SIZE)}</span>
                  <button onClick={() => setAppPage(p => p + 1)} disabled={appPage * APP_PAGE_SIZE >= filteredApps.length}
                    className="px-3 py-1.5 text-[10px] font-bold text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-30 transition-all cursor-pointer">Next →</button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── Application Preview Modal ── */}
      {previewApp && (
        <Modal onClose={() => setPreviewApp(null)} ariaLabel="Application Details">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-base font-black text-slate-900">Application Details</h2>
              <button aria-label="Close" title="Close" onClick={() => setPreviewApp(null)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Applicant Identity */}
              <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-lg font-black text-indigo-600">
                    {(previewApp.applicantName || '?').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{previewApp.applicantName || 'Unknown'}</p>
                  <p className="text-xs text-slate-500">{previewApp.applicantEmail || ''}{previewApp.applicantPhone ? ` • ${previewApp.applicantPhone}` : ''}</p>
                </div>
                <div className="ml-auto">
                  <AppStatusBadge status={previewApp.status} />
                </div>
              </div>
              {/* Detail grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block">Program</label>
                  <p className="text-sm text-slate-700">{previewApp.programType || '—'}</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block">Applied</label>
                  <p className="text-sm text-slate-700">{previewApp.appliedAt ? new Date(previewApp.appliedAt).toLocaleString() : '—'}</p>
                </div>
                {previewApp._type === 'match' && (
                  <div>
                    <label className="text-[10px] uppercase font-bold text-slate-400 block">Source</label>
                    <p className="text-sm text-slate-700">Admin Match</p>
                  </div>
                )}
                {previewApp.createdByName && (
                  <div>
                    <label className="text-[10px] uppercase font-bold text-slate-400 block">Matched By</label>
                    <p className="text-sm text-slate-700">{previewApp.createdByName}</p>
                  </div>
                )}
                {previewApp.notes && (
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Match Notes</label>
                    <p className="text-xs text-slate-600 bg-slate-50 rounded-xl p-3 whitespace-pre-wrap">{previewApp.notes}</p>
                  </div>
                )}
              </div>
              {previewApp.coverLetter && (
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Cover Letter</label>
                  <p className="text-xs text-slate-600 bg-slate-50 rounded-xl p-3 whitespace-pre-wrap">{previewApp.coverLetter}</p>
                </div>
              )}
              {previewApp.employerFeedback && (
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Employer Feedback</label>
                  <p className="text-xs text-slate-600 bg-amber-50 rounded-xl p-3 whitespace-pre-wrap">{previewApp.employerFeedback}</p>
                </div>
              )}
              {previewApp.adminNotes && (
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Admin Notes</label>
                  <p className="text-xs text-slate-600 bg-blue-50 rounded-xl p-3 whitespace-pre-wrap">{previewApp.adminNotes}</p>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ── AI Job Creation Modal ── */}
      {showAiModal && (
        <Modal onClose={() => setShowAiModal(false)} ariaLabel="AI Job Creation">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-indigo-600" />
                AI Job Creation
              </h2>
              <button aria-label="Close" title="Close" onClick={() => setShowAiModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-indigo-50 rounded-xl p-3">
                <p className="text-xs text-indigo-700 font-medium">Describe the job you want to create in natural language. The AI will extract key details and populate the form.</p>
              </div>
              <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} rows={4}
                placeholder="e.g. Create a Summer Camp Counselor position in Pennsylvania for 20 Jamaican counselors, starting May 2027. Housing included. Looking for applicants with childcare experience."
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 resize-none" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowAiModal(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                <button onClick={async () => {
                  if (!aiPrompt.trim()) return;
                  setAiLoading(true);
                  try {
                    const data = await safeFetch('/api/admin/jobs/ai-generate', {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ prompt: aiPrompt }),
                    });
                    if (data.success && data.fields) {
                      const f = data.fields;
                      setForm({
                        title: f.title || '', employerId: '', employerName: '',
                        location: `${f.city || ''}${f.state ? ', ' + f.state : ''}${f.country ? ', ' + f.country : ''}`.replace(/^,\s*/, ''),
                        jobType: f.jobType || 'seasonal',
                        description: f.description || prompt, requirements: '',
                        salary: '', compensation: '',
                        startDate: '', endDate: '', applicationDeadline: '',
                        country: f.country || '', programType: f.programs?.[0] || '',
                        programs: f.programs || [], industry: '',
                        positions: f.positions || 1, tags: '',
                      });
                      setShowAiModal(false);
                      setShowCreateModal(true);
                      showToast('AI generated job details — review before saving', 'success');
                    }
                  } catch (err: any) { showToast(err.message, 'error'); }
                  finally { setAiLoading(false); }
                }} disabled={!aiPrompt.trim() || aiLoading}
                  className="px-6 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark disabled:opacity-50 transition-all cursor-pointer shadow-lg shadow-brand-primary/20 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" />{aiLoading ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Bulk Status Update Modal ── */}
      {showBulkStatusModal && (
        <Modal onClose={() => !bulkActionLoading && setShowBulkStatusModal(false)} ariaLabel="Bulk Update Status" size="md">
          <div className="bg-white rounded-2xl shadow-2xl w-full" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5">
              <h2 className="text-sm font-black text-slate-900 mb-1">Bulk Update Status</h2>
              <p className="text-xs text-slate-500 mb-4">Update status for <strong>{selectedMatchRecords.length}</strong> selected record{selectedMatchRecords.length !== 1 ? 's' : ''}</p>
              <select value={bulkStatusValue} onChange={e => setBulkStatusValue(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 mb-4">
                <option value="">— Select status —</option>
                {ALL_INTEREST_STATUSES.filter(s => s.value !== 'all').map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowBulkStatusModal(false)} disabled={bulkActionLoading}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={handleBulkStatusChange} disabled={!bulkStatusValue || bulkActionLoading}
                  className="px-4 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark disabled:opacity-50 transition-all cursor-pointer flex items-center gap-1.5">
                  {bulkActionLoading ? <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" /> : <CheckCircle className="h-3.5 w-3.5" />}
                  {bulkActionLoading ? 'Updating...' : `Update ${selectedMatchRecords.length} Record${selectedMatchRecords.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
